
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { SavedBiblicalDefenseContent } from '@/components/BiblicalDefense/types';
import TextSection from '@/components/BiblicalDefense/components/TextSection';
import ScriptureSection from '@/components/BiblicalDefense/components/ScriptureSection';
import ObjectionsSection from '@/components/BiblicalDefense/components/ObjectionsSection';
import { cleanHashtags } from '@/components/BiblicalDefense/utils/objectionsParser';

export const renderBiblicalDefenseContent = (item: any, navigate: ReturnType<typeof useNavigate>) => {
  if (!item || !item.content) return <p>No content available</p>;
  
  const content = item.content as SavedBiblicalDefenseContent;
  const response = content.response;
  
  const hasStructuredObjections = 
    response.objections && 
    (response.objections.includes("Objection 1:") || 
     response.objections.includes("Objection:"));

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <Button 
          variant="ghost" 
          className="w-fit p-0 h-auto mb-2" 
          onClick={() => navigate('/dashboard')}
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to All Saved Responses
        </Button>
        <CardTitle className="flex items-center gap-2 text-heaven-gold mt-3">
          <BookOpen className="h-5 w-5" />
          Biblical Defense
        </CardTitle>
        <CardDescription className="text-lg font-medium mt-1">
          {content.question}
        </CardDescription>
        <CardDescription className="text-sm text-gray-500">
          Saved on {new Date(content.createdAt).toLocaleDateString()}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-8 pt-6">
        {/* Direct Answer Section */}
        <TextSection 
          number={1} 
          title="Direct Answer" 
          content={response.directAnswer} 
        />
        
        {/* Biblical Foundation Section */}
        <TextSection 
          number={2} 
          title="Biblical Foundation" 
          content={response.biblicalFoundation} 
        />
        
        {/* Scripture References Section */}
        <ScriptureSection scriptureReferences={response.scriptureReferences} />
        
        {/* Theological Reasoning Section */}
        <TextSection 
          number={4} 
          title="Logical, Philosophical, and Moral Reasoning" 
          content={response.theologicalReasoning} 
        />
        
        {/* Historical Context Section */}
        <TextSection 
          number={5} 
          title="Historical and Scientific Support" 
          content={response.historicalContext} 
        />
        
        {/* Common Objections Section */}
        {hasStructuredObjections ? (
          <ObjectionsSection objections={response.objections} />
        ) : (
          <TextSection 
            number={6} 
            title="Addressing Common Objections" 
            content={response.objections} 
          />
        )}
        
        {/* Final Encouragement Section */}
        <TextSection 
          number={7} 
          title="Final Encouragement" 
          content={response.finalEncouragement} 
          isLast={true}
        />
      </CardContent>
    </Card>
  );
};
